import { useEffect, useState } from 'react';
import {
  type ApproverDecision,
  type DecisionBatch,
  type Disposition,
  SEVERITIES,
  type Treatment,
} from '@zevik/schemas';
import {
  AFFORDANCES,
  buildDecisionRequest,
  type DecisionDraft,
  DISPOSITIONS,
  draftProblems,
  EDIT_LABELS,
  editableSlotsFor,
  emptyDraft,
  TREATMENT_LABELS,
  TREATMENTS,
} from '../review/decision';
import type { FindingEntry } from '../api/client';

/**
 * The decision control (docs/DECISIONS.md ZD-112).
 *
 * **Every affordance is read off `AFFORDANCES` rather than written into this
 * markup**, so what the screen offers and what the schema permits are one
 * declaration with a comparator over it (`decision.test.ts`), rather than two
 * expressions that agree today. A treatment picker that appears on `reject`
 * because somebody forgot a condition here would be a decision the API refuses;
 * a treatment picker that vanished on `accept` would be one it cannot record.
 *
 * There is **no state picker**. A finding's state is derived from the pair a
 * reviewer answers (ZD-061), so `approved` and `accepted_risk` are consequences
 * rather than choices, and offering them would be a third vocabulary.
 *
 * **The rewrite fields are the slots `SLOT_REVIEWER_EDITABLE` opens**, read off
 * the schema through `EDITABLE_SLOTS` (ZD-125 §1). A rewrite is laid beside the
 * pipeline's sentence, never over it, and reaches this screen, the finding
 * and the report. Until `sds-12o` there was no edit control, and the reason
 * was on screen: an edit was accepted by the API and reached nothing.
 */

interface Props {
  finding: FindingEntry;
  standing: ApproverDecision | undefined;
  /** The group the standing decision was made in, where it was (ZD-127 §3). */
  batch: DecisionBatch | undefined;
  onRecord: (draft: DecisionDraft) => Promise<void>;
  /** Why recording is unavailable, where it is — e.g. reading a fixture. */
  unavailable: string | null;
  /**
   * Bumped by the workspace when a person signs back in after a session ended
   * mid-decision, so the refusal that said so is cleared while the draft it
   * refused stays (`credential/session.ts`).
   */
  sessionRestored: number;
}

export function DecisionControl({
  finding,
  standing,
  batch,
  onRecord,
  unavailable,
  sessionRestored,
}: Props): React.JSX.Element {
  const [draft, setDraft] = useState<DecisionDraft>(emptyDraft);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    setFailure(null);
  }, [sessionRestored]);

  const affordance = AFFORDANCES[draft.disposition];
  const problems = draftProblems(draft, finding.statement);
  const editable = editableSlotsFor(finding.statement);

  const setDisposition = (disposition: Disposition): void => {
    /*
     * Clearing what the new disposition forbids rather than leaving it set and
     * refusing later. A treatment left over from `accept` under a `reject` would
     * be a field the reviewer cannot see and cannot clear, and the refusal would
     * name something not on screen.
     */
    const next = AFFORDANCES[disposition];
    setDraft((current) => ({
      disposition,
      treatment: next.treatment === 'forbidden' ? null : current.treatment,
      reason: current.reason,
      dissent: next.dissent === 'forbidden' ? null : current.dissent,
      // A rewrite is kept across a change of mind: a rejection refuses it in
      // `draftProblems` with its reason rather than silently dropping it.
      edits: current.edits,
    }));
  };

  const submit = async (): Promise<void> => {
    setFailure(null);
    setSending(true);
    try {
      // Built here as well as sent, so a draft the builder refuses never leaves
      // the browser — `draftProblems` is what the screen shows and this is the
      // last gate.
      buildDecisionRequest(finding.finding, finding, draft, finding.statement);
      await onRecord(draft);
      setDraft(emptyDraft());
    } catch (error) {
      setFailure(error instanceof Error ? error.message : String(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="decide">
      <h3>Your decision</h3>

      {standing === undefined ? null : (
        <p className="notice">
          You currently have this recorded as <strong>{standing.disposition}</strong>
          {standing.treatment === null ? '' : ` — ${TREATMENT_LABELS[standing.treatment]}`}
          {standing.reason === null ? '' : `, because ${standing.reason}`}.
          {/*
           * **A decision made in a group says so, and says the group's reason**
           * (ZD-127 §3). The record answers *was this finding considered
           * individually?* — a batched decision carries its group's id and may
           * carry no reason of its own — and a screen that printed it as a
           * decision like any other would be a batch hiding what it contains.
           * The count is the group as recorded; a member re-decided since is
           * still counted, because the group was that size when it was made.
           */}
          {standing.batch_id === null ? null : (
            <>
              {' '}
              <strong>
                This was one of a group of{' '}
                {batch === undefined ? 'several' : String(batch.finding_keys.length)} findings
                decided together
              </strong>
              {batch?.reason == null
                ? ', with no reason given for the group'
                : `; the reason given for the group was: ${batch.reason}`}
              . It was not considered on its own.
            </>
          )}{' '}
          Recording another decision supersedes that one; both are kept.
        </p>
      )}

      <fieldset>
        <legend>Is this true of their system?</legend>
        {DISPOSITIONS.map((disposition) => (
          <label className="choice" key={disposition}>
            <input
              type="radio"
              name="disposition"
              checked={draft.disposition === disposition}
              onChange={() => {
                setDisposition(disposition);
              }}
            />
            <span>{AFFORDANCES[disposition].label}</span>
          </label>
        ))}
      </fieldset>

      {affordance.treatment === 'required' ? (
        <fieldset>
          <legend>What happens about it?</legend>
          {TREATMENTS.map((treatment: Treatment) => (
            <label className="choice" key={treatment}>
              <input
                type="radio"
                name="treatment"
                checked={draft.treatment === treatment}
                onChange={() => {
                  setDraft((current) => ({ ...current, treatment }));
                }}
              />
              <span>{TREATMENT_LABELS[treatment]}</span>
            </label>
          ))}
        </fieldset>
      ) : null}

      <div>
        <label className="field" htmlFor="reason">
          {affordance.reason === 'required' ? 'Why (required)' : 'Why (optional)'}
        </label>
        <textarea
          id="reason"
          value={draft.reason}
          onChange={(event) => {
            setDraft((current) => ({ ...current, reason: event.target.value }));
          }}
        />
      </div>

      {draft.disposition === 'reject' || editable.length === 0 ? null : (
        <fieldset className="rewrite">
          <legend>Rewrite for this client (optional)</legend>
          <p className="notice">
            What you write here is shown beside the pipeline’s own wording, never instead of it, and
            is printed under the finding in the report as yours.
          </p>
          {editable.map((slot) => (
            <div key={slot}>
              <label className="field" htmlFor={`edit-${slot}`}>
                {EDIT_LABELS[slot] ?? slot}
              </label>
              <textarea
                id={`edit-${slot}`}
                value={draft.edits[slot] ?? ''}
                onChange={(event) => {
                  const text = event.target.value;
                  setDraft((current) => ({
                    ...current,
                    edits: { ...current.edits, [slot]: text },
                  }));
                }}
              />
            </div>
          ))}
        </fieldset>
      )}

      {affordance.dissent === 'permitted' ? (
        <div>
          <label className="field" htmlFor="dissent">
            You would band it differently (optional)
          </label>
          <select
            id="dissent"
            value={draft.dissent?.band ?? ''}
            onChange={(event) => {
              const band = event.target.value;
              setDraft((current) => ({
                ...current,
                dissent: band === '' ? null : { band, reason: current.dissent?.reason ?? '' },
              }));
            }}
          >
            <option value="">Leave the band as computed</option>
            {SEVERITIES.map((band) => (
              <option key={band} value={band}>
                {band}
              </option>
            ))}
          </select>
          {draft.dissent === null ? null : (
            <>
              <p className="notice">
                Recorded beside the computed band and never replacing it. A band is derived, so a
                second one asserted by hand would be a scoring method with one row of input.
              </p>
              <textarea
                value={draft.dissent.reason}
                aria-label="why you would band it differently"
                onChange={(event) => {
                  const reason = event.target.value;
                  setDraft((current) => ({
                    ...current,
                    dissent: current.dissent === null ? null : { ...current.dissent, reason },
                  }));
                }}
              />
            </>
          )}
        </div>
      ) : null}

      {problems.length > 0 ? (
        <div className="decide__problems">
          {problems.map((problem) => (
            <p key={problem}>{problem}</p>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        className="submit"
        disabled={problems.length > 0 || sending || unavailable !== null}
        onClick={() => {
          void submit();
        }}
      >
        {sending ? 'Recording…' : 'Record this decision'}
      </button>

      {unavailable === null ? null : <p className="notice">{unavailable}</p>}
      {failure === null ? null : <p className="notice notice--bad">{failure}</p>}
    </section>
  );
}
